import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { groupFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal } from '../../components/ui';
import { SkeletonTable } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import { translateError } from '../../utils/errorMessages';

interface IGroup {
  _id: string;
  name: string;
  description: string;
  managed?: { kind: 'course' | 'activity' } | null;
  memberCount?: number;
}

const GroupsListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Permission checks
  const canCreate = hasPermission('groups:write');
  const canEdit = hasPermission('groups:write');
  const canDelete = hasPermission('groups:delete');

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.groups.list();
      setGroups(data as IGroup[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleDeleteClick = (group: IGroup) => {
    if (!canDelete) return;
    setDeleteId(group._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await api.groups.delete(deleteId);
      fetchGroups();
      addToast(t('groups.deleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'groups.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const columns: Column<IGroup>[] = useMemo(() => [
    {
      header: t('common.name'),
      key: 'name',
      // Managed groups are virtual views of an enrollment / activity roster.
      render: (group) => (
        <>
          {group.name}
          {group.managed && (
            <>
              {' '}
              <span className="badge" title={t(group.managed.kind === 'course' ? 'groups.managedCourseNote' : 'groups.managedActivityNote')}>
                {t('groups.managedBadge')}
              </span>
            </>
          )}
        </>
      ),
    },
    {
      header: t('common.description'),
      key: 'description',
    },
    {
      header: t('groups.memberCount'),
      render: (group) => group.memberCount ?? 0,
    },
  ], [t]);

  const filteredGroups = useMemo(() => applyFilters(groups, filters), [groups, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <PageHeader
        title={t('groups.title')}
        helpKey="help.admin.groups"
        action={canCreate ? <Link to="/admin/groups/create" className="btn btn--primary btn--md">{t('groups.createNew')}</Link> : undefined}
      />

      <FilterBar fields={groupFields} onFilterChange={handleFilterChange} />

      {loading ? (
        <SkeletonTable rows={5} columns={3} />
      ) : filteredGroups.length === 0 ? (
        <EmptyState
          title={t('groups.emptyTitle')}
          description={t('groups.emptyDescription')}
          action={canCreate ? { label: t('groups.createNew'), href: '/admin/groups/create' } : undefined}
        />
      ) : (
        <DataTable
          data={filteredGroups}
          columns={columns}
          editLink={canEdit ? (group) => `/admin/groups/${group._id}/edit` : undefined}
          onDelete={canDelete ? handleDeleteClick : undefined}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('groups.deleteTitle')}
        message={t('groups.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default GroupsListPage;
